import { useMutation } from "@apollo/client";
import {
    Avatar,
    Button,
    FileInput,
    Group,
    Modal,
    TextInput
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons";
import { UPDATE_PROFILE } from "apollo/mutuations";
import { useEffect, useState } from "react";
import { MAX_FILE_SIZE } from "utiles/url";

export default function EditProfileModal({
  opened,
  onClose,
  user,
  onUserUpdate,
}) {
  const [previewImage, setPreviewImage] = useState(null);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      profile_image: null,
    },
    validate: {
      name: (value) =>
        value.length < 3 ? "Name must be at least 3 characters" : null,
      email: (value) =>
        /^\S+@\S+$/.test(value) ? null : "Invalid email format",
    },
  });

  // Load user details on open
  useEffect(() => {
    if (user) {
      form.setValues({
        name: user.name,
        email: user.email,
        profile_image: null, 
      });
      setPreviewImage(user.profile_image); 
    }
  }, [user]);

  const handleImageUpload = (file) => {
    if (file) {

      if (file.size > MAX_FILE_SIZE) {
        showNotification({
          color: "red",
          title: "File too large",
          message: "Maximum file size is 1 MB",
        });
        form.setFieldValue("profile_image", null);
        setPreviewImage(user.profile_image);
        return;
      }

      form.setFieldValue("profile_image", file);

      const reader = new FileReader();
      reader.onload = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(user.profile_image); 
    }
  };

  const [editUser, { loading }] = useMutation(UPDATE_PROFILE);

  const submit = () => {
    const variables = {
      name: form.values.name,
      email: form.values.email,
      profile_image: form.values.profile_image,
    };

    editUser({
      variables,
      onCompleted(data) {
        const updatedUser = {
          ...user,
          ...data.selfUpdateUser,
        };
        console.log(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        if (onUserUpdate) onUserUpdate(updatedUser);

        showNotification({
          color: "green",
          title: "Success",
          message: "Profile Updated Successfully",
        });

        onClose();
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Update Profile",
        });
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      styles={{
        content: { maxWidth: "600px" },
      }}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <div style={{ textAlign: "center" }}>
          <Avatar
            src={previewImage || "https://b2b.ant.et/images/defaults/user.jpg"}
            size={100}
            radius="xl"
            alt="Profile Image"
          />
        </div>

        <TextInput
          label="Name"
          {...form.getInputProps("name")}
          required
          mb="sm"
        />
        <TextInput label="Email" {...form.getInputProps("email")} mb="sm" />

        <FileInput
          label="Profile Image"
          placeholder="Upload Image"
          icon={<IconUpload size={18} />}
          accept="image/*"
          onChange={handleImageUpload}
        />

        <Group position="right" mt="lg">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            style={{
              backgroundColor: "#FF6A00",
              color: "#FFFFFF",
            }}
          >
            Save Changes
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
